// Client layer (TS-2.5.03). One project's detail card per BRD section 8.2: name, status pill,
// owner, a % bar, description and either a milestone timeline or the target go-live fallback -
// the either/or is driven by `milestones.length === 0` (decomposition.md section 8.7 point 8;
// 45 of 73 live projects carry no milestones, so this branch is common, not an edge case).
//
// D-280 additions: a Lead/Business owner line (the effective business owner resolved client-side
// from the same projects+products join the Team/Product toggle already reads - see
// hooks/useProjectProductJoin.ts and lib/resolveProjectOwners.ts) and the assigned product's name
// when one exists, plus an explicit "Expand" button opening a read-only Modal with the larger view
// (ProjectDetailModal.tsx). `ownerInfo` is optional so this component keeps working, degraded to
// "no owner info yet", for any caller that has not (yet) resolved the join - it is never required
// to render at all.
import { useState, type CSSProperties } from "react";
import { resolveColor } from "./lib/colorTokens";
import { readableTextColor } from "./lib/contrast";
import { MilestoneTimeline } from "./MilestoneTimeline";
import { ProjectDetailModal } from "./ProjectDetailModal";
import type { ProjectOwnerInfo } from "./lib/resolveProjectOwners";
import type { ProjectView } from "../../api/generated/contracts";
import { strings } from "../../strings";

const C = strings.roadmap.detailCard;
const NO_OWNER_INFO: ProjectOwnerInfo = { productName: null, businessOwner: null };

export interface ProjectDetailCardProps {
  readonly project: ProjectView;
  readonly ownerInfo?: ProjectOwnerInfo;
}

export function ProjectDetailCard({ project, ownerInfo = NO_OWNER_INFO }: ProjectDetailCardProps) {
  const [expanded, setExpanded] = useState(false);
  const percent = project.percentComplete;
  // D-255 (owner: "a literal flag or a slightly red background" for a milestone-less project).
  // Red was rejected - it collides with this app's Off Track/Blocked status semantics - in favour
  // of the house warning palette (WarningsBanner's gold-keyline/wash, D-252/D-254's
  // `--eg-color-accent` treatment) PLUS a text badge, never colour alone (D-247's dashed-outline
  // precedent for "no signal is colour-only"). Same detection point the card's own
  // timeline-vs-go-live branch already uses two lines below.
  const hasNoMilestones = project.milestones.length === 0;

  return (
    <article
      className={hasNoMilestones ? "eg-detail-card eg-detail-card--no-milestones" : "eg-detail-card"}
      aria-labelledby={`eg-detail-${project.key}`}
    >
      <div className="eg-detail-card__header">
        {/* D-300: the title and badge wrap TOGETHER inside their own group, so the expand button
            beside them always stays on the first line instead of being pushed under a long name. */}
        <div className="eg-detail-card__title">
          <h3 id={`eg-detail-${project.key}`}>{project.name}</h3>
          {hasNoMilestones && (
            <span className="eg-detail-card__badge eg-detail-card__badge--warning">
              <span aria-hidden="true">⚑</span> {strings.roadmap.noMilestones}
            </span>
          )}
        </div>
        {/* Icon-only by owner request - the visible sentence duplicated the title beside it. The
            full "Expand <name> details" stays as the ACCESSIBLE name (aria-label), so tests that
            query by role+name and screen-reader users are unaffected; the glyph is decoration. */}
        <button
          type="button"
          className="eg-detail-card__expand"
          aria-label={C.expand(project.name)}
          title={C.expand(project.name)}
          onClick={() => setExpanded(true)}
        >
          <span aria-hidden="true">⤢</span>
        </button>
      </div>
      <span
        className="eg-detail-card__pill"
        // Same per-value computed text colour as the board chips (D-247) - the pill's background
        // is the same admin-controlled vocabulary hex. roadmap.css reads both custom properties.
        style={
          {
            "--eg-chip-color": resolveColor(project.status.colorHex),
            "--eg-chip-ink": readableTextColor(project.status.colorHex),
          } as CSSProperties
        }
      >
        {project.status.canonical}
      </span>
      <p className="eg-detail-card__meta">
        {project.team} · {project.owner}
        {ownerInfo.productName ? ` · ${ownerInfo.productName}` : ""}
      </p>
      {/* D-280: Lead is the same value the meta line above already carries as "owner" (relabelled
          on the project form, not here - ProjectView.owner is unchanged) - repeated here, named,
          beside the resolved Business owner so the two never read as one ambiguous "owner". */}
      <p className="eg-detail-card__owners">
        {C.lead}: {project.owner || C.noOwner} · {C.businessOwner}: {ownerInfo.businessOwner || C.noOwner}
      </p>
      <progress
        className="eg-detail-card__progress"
        value={percent ?? 0}
        max={100}
        aria-label={`${project.name} percent complete`}
      />
      <span>{percent === null ? "—" : `${Math.round(percent)}%`}</span>
      <p className="eg-detail-card__description">{project.description}</p>
      {!hasNoMilestones ? (
        <MilestoneTimeline milestones={project.milestones} />
      ) : (
        <p className="eg-detail-card__golive">Target go-live: {project.goLive.label}</p>
      )}
      <ProjectDetailModal open={expanded} project={project} ownerInfo={ownerInfo} onClose={() => setExpanded(false)} />
    </article>
  );
}
